// src/components/admin/contas/SubUsuarios.tsx
// GRAIA Admin — Grid de cards de usuários + Modal completo de assinatura
import React, { useEffect, useState, useCallback } from 'react';
import {
    Search, ChevronLeft, ChevronRight, X,
    Plus, CreditCard, CheckCircle, Clock, Ban,
    RefreshCw, Package, AlertTriangle, Zap, Shield,
    CalendarDays
} from 'lucide-react';
import './SubUsuarios.css';

// ── Tipos ─────────────────────────────────────────────────────
type AccessFilter = 'all' | 'free' | 'com_acesso' | 'vip' | 'vendor';

interface AccessStats {
    total: number;
    vip: number;
    vendor: number;
    com_acesso: number;
    free: number;
}

interface UserRow {
    _id: string;
    username: string;
    email: string;
    role: string;
    isLegacyUser: boolean;
    grantedFeatures: string[];
    revokedFeatures: string[];
    vipAccess?: { active: boolean; expiresAt?: string | null };
    editorConfig?: { active: boolean };
    createdAt: string;
    accessSummary?: string;
    activePurchaseCount?: number;
}

interface PurchaseRow {
    _id: string;
    productId: { _id: string; title?: string; name?: string; type: string; active: boolean } | null;
    accessType: 'lifetime' | 'subscription';
    expiresAt: string | null;
    active: boolean;
    grantedByAdmin?: boolean;
    grantedAt?: string;
    createdAt: string;
}

interface EffectiveFeature {
    status: 'active' | 'granted' | 'revoked' | 'locked';
    source: string;
}

interface UserAccessProfile {
    user: UserRow & { vipAccess?: any; vendorConfig?: any };
    access: { hasFullAccess: boolean; hasVip: boolean; hasVendor: boolean; hasAccessAll: boolean; isLegacy: boolean; summary: string };
    effectiveFeatures: Record<string, EffectiveFeature>;
    activePurchases: PurchaseRow[];
    expiredPurchases: PurchaseRow[];
    totalPurchases: number;
}

// ── Constantes ────────────────────────────────────────────────
const FEATURES = ['questions', 'stats', 'planner', 'groups'] as const;
const FEATURE_LABELS: Record<string, string> = {
    questions: 'Questões', stats: 'Estatísticas', planner: 'Planner', groups: 'Grupos',
};

const DURATION_OPTIONS = [
    { value: '1w', label: '1 semana' },
    { value: '1m', label: '1 mês' },
    { value: '2m', label: '2 meses' },
    { value: '3m', label: '3 meses' },
    { value: '4m', label: '4 meses' },
    { value: '6m', label: '6 meses' },
    { value: '1y', label: '1 ano' },
    { value: 'lifetime', label: 'Vitalício' },
];

const TYPE_COLOR: Record<string, string> = {
    access_all: '#003BD1', cycle: '#3b82f6', combo: '#8b5cf6', deck: '#f59e0b',
};
const TYPE_LABEL: Record<string, string> = {
    access_all: 'Acesso Total', cycle: 'Ciclo', combo: 'Combo', deck: 'Deck',
};
const SOURCE_LABEL: Record<string, string> = {
    vip: 'VIP', vendor: 'Vendedor', access_all: 'Acesso Total',
    purchase: 'Compra', legacy: 'Legado',
    manual_grant: 'Concedido manualmente', manual_revoke: 'Revogado manualmente', none: '—',
};

const PAGE_SIZE = 20;

function fmtDate(d?: string | null) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-BR');
}

function productLabel(p: PurchaseRow['productId']) {
    if (!p) return 'Produto removido';
    return p.title || p.name || p._id;
}

function initials(name: string) {
    return name.slice(0, 2).toUpperCase();
}

function isExpired(d?: string | null) {
    if (!d) return false;
    return new Date(d) < new Date();
}

function statusBarClass(summary: string) {
    if (['VIP', 'Vendedor', 'Acesso Total'].includes(summary)) return 'full';
    if (summary === 'Sem Acesso') return 'none';
    return 'partial';
}

// ══════════════════════════════════════════════════════════════
const SubUsuarios: React.FC = () => {
    const token = localStorage.getItem('accessToken');
    const [users, setUsers] = useState<UserRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);

    // Filtros
    const [accessFilter, setAccessFilter] = useState<AccessFilter>('all');
    const [stats, setStats] = useState<AccessStats | null>(null);

    // Modal
    const [subModal, setSubModal] = useState<UserRow | null>(null);
    const [subProfile, setSubProfile] = useState<UserAccessProfile | null>(null);
    const [subLoading, setSubLoading] = useState(false);
    const [subTab, setSubTab] = useState<'overview' | 'products' | 'features'>('overview');

    // Grant
    const [products, setProducts] = useState<{ _id: string; name: string; type: string }[]>([]);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [selectedDuration, setSelectedDuration] = useState('lifetime');
    const [grantLoading, setGrantLoading] = useState(false);
    const [grantMsg, setGrantMsg] = useState<{ ok: boolean; text: string } | null>(null);

    // Feature actions
    const [featureLoading, setFeatureLoading] = useState<string | null>(null);

    // ── Usuários ─────────────────────────────────────────────
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                limit: String(PAGE_SIZE),
                ...(search ? { search } : {}),
                ...(accessFilter !== 'all' ? { accessFilter } : {})
            });
            const res = await fetch(`/api/admin/subscriptions/users?${params}`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) {
                setUsers(data.users);
                setTotal(data.total);
                if (data.stats) setStats(data.stats);
            }
        } finally { setLoading(false); }
    }, [token, page, search, accessFilter]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    // ── Produtos (lazy) ───────────────────────────────────────
    const fetchProducts = useCallback(async () => {
        if (products.length > 0) return;
        try {
            const res = await fetch('/api/admin/marketplace/products', { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.products) setProducts(data.products.map((p: any) => ({ _id: p._id, name: p.title || p.name, type: p.type })));
        } catch { }
    }, [token, products.length]);

    // ── Modal ─────────────────────────────────────────────────
    const openModal = useCallback(async (user: UserRow) => {
        setSubModal(user);
        setSubProfile(null);
        setSubTab('overview');
        setGrantMsg(null);
        setSelectedProduct('');
        setSelectedDuration('lifetime');
        setSubLoading(true);
        fetchProducts();
        try {
            const res = await fetch(`/api/admin/user-subscriptions/${user._id}`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) setSubProfile(data);
        } finally { setSubLoading(false); }
    }, [token, fetchProducts]);

    const refreshProfile = useCallback(async (userId: string) => {
        try {
            const res = await fetch(`/api/admin/user-subscriptions/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) setSubProfile(data);
        } catch { }
    }, [token]);

    // ── Grant produto ─────────────────────────────────────────
    const handleGrant = async () => {
        if (!subModal || !selectedProduct) return;
        setGrantLoading(true); setGrantMsg(null);
        try {
            const res = await fetch(`/api/admin/user-subscriptions/${subModal._id}/grant-product`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ productId: selectedProduct, duration: selectedDuration })
            });
            const data = await res.json();
            setGrantMsg({ ok: data.success, text: data.message });
            if (data.success) { setSelectedProduct(''); setSelectedDuration('lifetime'); await refreshProfile(subModal._id); fetchUsers(); }
        } finally { setGrantLoading(false); }
    };

    // ── Revogar produto ───────────────────────────────────────
    const handleRevoke = async (purchaseId: string) => {
        if (!subModal) return;
        const res = await fetch(`/api/admin/user-subscriptions/${subModal._id}/revoke-product/${purchaseId}`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) { await refreshProfile(subModal._id); fetchUsers(); }
    };

    // ── Feature actions ───────────────────────────────────────
    const doFeature = useCallback(async (userId: string, action: 'grant' | 'revoke', feature: string) => {
        setFeatureLoading(`${action}-${feature}`);
        try {
            await fetch(`/api/admin/features/${userId}/${action}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ feature })
            });
            fetchUsers();
            if (subModal?._id === userId) refreshProfile(userId);
        } finally { setFeatureLoading(null); }
    }, [token, fetchUsers, refreshProfile, subModal]);

    // ── Sync features ─────────────────────────────────────────
    const handleSync = async () => {
        if (!subModal) return;
        await fetch(`/api/admin/user-subscriptions/${subModal._id}/sync-features`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }
        });
        refreshProfile(subModal._id);
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);

    // ═════════════════════════════════════════════════════════
    return (
        <div>
            {/* ── Toolbar: search + filtros ─────────────────── */}
            <div className="su-toolbar">
                <div className="su-search-wrap">
                    <Search size={14} />
                    <input
                        className="su-search"
                        placeholder="Buscar por nome ou email..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                    />
                </div>
                <div className="su-filters">
                    {([
                        { key: 'all',        label: 'Todos',      count: stats?.total },
                        { key: 'free',       label: 'Free',       count: stats?.free },
                        { key: 'com_acesso', label: 'Com Acesso', count: stats?.com_acesso },
                        { key: 'vip',        label: 'VIP',        count: stats?.vip },
                        { key: 'vendor',     label: 'Vendedor',   count: stats?.vendor },
                    ] as { key: AccessFilter; label: string; count?: number }[]).map(f => (
                        <button
                            key={f.key}
                            className={`su-filter-chip ${accessFilter === f.key ? 'active' : ''}`}
                            onClick={() => { setAccessFilter(f.key); setPage(1); }}
                        >
                            {f.label}
                            {f.count !== undefined && (
                                <span className="su-chip-count">{f.count.toLocaleString('pt-BR')}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Grid ─────────────────────────────────────── */}
            <div className="su-grid">
                {loading ? (
                    <div className="su-loading">
                        <div className="su-spinner" /> Carregando usuários...
                    </div>
                ) : users.length === 0 ? (
                    <div className="su-empty">Nenhum usuário encontrado.</div>
                ) : users.map(user => (
                    <div key={user._id} className="su-card">
                        <div className="su-card-top">
                            <div className="su-avatar">{initials(user.username)}</div>
                            <div className="su-card-info">
                                <div className="su-card-name">{user.username}</div>
                                <div className="su-card-email">{user.email}</div>
                            </div>
                        </div>

                        <div className="su-card-badges">
                            {/* Role badges — fixos, sempre mostram se aplicável */}
                            {user.role === 'admin' && <span className="su-badge admin">Admin</span>}
                            {user.role === 'vendor' && <span className="su-badge vendor">Vendor</span>}
                            {user.editorConfig?.active && <span className="su-badge editor">Editor</span>}
                            {user.isLegacyUser && <span className="su-badge legacy">Legado</span>}

                            {/* Access badge — derivado de accessSummary, separado dos roles */}
                            {user.role !== 'admin' && user.accessSummary === 'VIP' && (
                                <span className="su-badge vip">VIP</span>
                            )}
                            {user.role !== 'admin' && user.accessSummary === 'Acesso Total' && (
                                <span className="su-badge access-full">Acesso Total</span>
                            )}
                            {user.role !== 'admin' && user.accessSummary === 'Acesso Parcial' && (
                                <span className="su-badge access-partial">Com Acesso</span>
                            )}
                            {user.role !== 'admin' && user.role !== 'vendor' && !user.isLegacyUser &&
                                (user.accessSummary === 'Sem Acesso' || !user.accessSummary) && (
                                <span className="su-badge inactive">Free</span>
                            )}
                        </div>

                        <div className="su-card-date">
                            <CalendarDays size={11} />
                            {fmtDate(user.createdAt)}
                        </div>

                        <button className="su-card-action" onClick={() => openModal(user)}>
                            <CreditCard size={13} /> Gerenciar Assinatura
                        </button>
                    </div>
                ))}
            </div>

            {/* ── Paginação ─────────────────────────────────── */}
            {totalPages > 1 && (
                <div className="su-pagination">
                    <button className="su-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                        <ChevronLeft size={14} />
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                        return <button key={p} className={`su-page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>;
                    })}
                    <button className="su-page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                        <ChevronRight size={14} />
                    </button>
                </div>
            )}

            {/* ══════════════════════════════════════════════
                MODAL — Gerenciar Assinatura
            ══════════════════════════════════════════════ */}
            {subModal && (
                <div className="su-overlay" onClick={() => setSubModal(null)}>
                    <div className="su-modal" onClick={e => e.stopPropagation()}>

                        {/* Header */}
                        <div className="su-modal-header">
                            <div>
                                <p className="su-modal-title">Gerenciar Assinatura</p>
                                <p className="su-modal-sub">{subModal.username} · {subModal.email}</p>
                            </div>
                            <button className="su-modal-close" onClick={() => setSubModal(null)}>
                                <X size={15} />
                            </button>
                        </div>

                        {subLoading ? (
                            <div className="su-loading" style={{ minHeight: 200 }}>
                                <div className="su-spinner" /> Carregando perfil...
                            </div>
                        ) : subProfile ? (<>

                            {/* Status bar */}
                            <div className={`su-status-bar ${statusBarClass(subProfile.access.summary)}`}>
                                {subProfile.access.hasFullAccess
                                    ? <CheckCircle size={15} />
                                    : subProfile.access.summary === 'Sem Acesso'
                                        ? <AlertTriangle size={15} />
                                        : <Shield size={15} />}
                                <span className="su-status-label">{subProfile.access.summary}</span>
                                {subProfile.access.hasVip && (
                                    <span className="su-status-detail">
                                        {subProfile.user.vipAccess?.expiresAt
                                            ? `· VIP expira ${fmtDate(subProfile.user.vipAccess.expiresAt)}`
                                            : '· VIP Vitalício'}
                                    </span>
                                )}
                                <button className="su-sync-btn" onClick={handleSync}>
                                    <RefreshCw size={11} /> Sync features
                                </button>
                            </div>

                            {/* Sub-tabs */}
                            <div className="su-modal-tabs">
                                {([
                                    { id: 'overview', label: 'Visão Geral', icon: Zap },
                                    { id: 'products', label: 'Produtos', icon: Package, count: subProfile.activePurchases.length },
                                    { id: 'features', label: 'Features', icon: Shield },
                                ] as const).map(tab => (
                                    <button key={tab.id}
                                        className={`su-modal-tab ${subTab === tab.id ? 'active' : ''}`}
                                        onClick={() => setSubTab(tab.id)}>
                                        <tab.icon size={13} />
                                        {tab.label}
                                        {'count' in tab && (
                                            <span className="su-tab-badge">{tab.count}</span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Body */}
                            <div className="su-modal-body">

                                {/* ── Visão Geral ─────────────────── */}
                                {subTab === 'overview' && (
                                    <div>
                                        <div className="su-summary-cards">
                                            {[
                                                { label: 'Produtos ativos', value: subProfile.activePurchases.length, color: 'var(--sub-green)' },
                                                { label: 'Expirados/revogados', value: subProfile.expiredPurchases.length, color: 'var(--sub-text-muted)' },
                                                { label: 'Total histórico', value: subProfile.totalPurchases, color: '#3b82f6' },
                                            ].map(c => (
                                                <div key={c.label} className="su-summary-card">
                                                    <div className="su-summary-value" style={{ color: c.color }}>{c.value}</div>
                                                    <div className="su-summary-label">{c.label}</div>
                                                </div>
                                            ))}
                                        </div>

                                        {subProfile.activePurchases.length === 0 ? (
                                            <div className="su-empty-state">Nenhum produto com acesso ativo</div>
                                        ) : (
                                            <div className="su-purchase-list">
                                                {subProfile.activePurchases.map(p => (
                                                    <div key={p._id} className="su-purchase-item">
                                                        <div className="su-purchase-dot" style={{ background: TYPE_COLOR[p.productId?.type || ''] || '#6b7280' }} />
                                                        <div className="su-purchase-info">
                                                            <div className="su-purchase-name">{productLabel(p.productId)}</div>
                                                            <div className="su-purchase-meta">
                                                                <span className="su-purchase-type" style={{
                                                                    background: (TYPE_COLOR[p.productId?.type || ''] || '#6b7280') + '22',
                                                                    color: TYPE_COLOR[p.productId?.type || ''] || '#6b7280',
                                                                }}>{TYPE_LABEL[p.productId?.type || ''] || p.productId?.type}</span>
                                                                {p.accessType === 'lifetime'
                                                                    ? <span className="su-lifetime">Vitalício</span>
                                                                    : <span><Clock size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />Expira {fmtDate(p.expiresAt)}</span>}
                                                                {p.grantedByAdmin && <span className="su-admin-tag">· admin</span>}
                                                            </div>
                                                        </div>
                                                        <button className="su-btn danger" style={{ fontSize: '0.74rem', padding: '5px 9px' }}
                                                            onClick={() => handleRevoke(p._id)}>
                                                            <Ban size={11} /> Revogar
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ── Produtos ────────────────────── */}
                                {subTab === 'products' && (
                                    <div>
                                        {/* Form de concessão */}
                                        <div className="su-grant-form">
                                            <p className="su-grant-form-title">Conceder acesso</p>
                                            <div className="su-grant-row">
                                                <div className="su-form-group">
                                                    <label>Produto</label>
                                                    <select value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)}>
                                                        <option value="">Selecione um produto...</option>
                                                        {products.map(p => (
                                                            <option key={p._id} value={p._id}>
                                                                {p.name} ({TYPE_LABEL[p.type] || p.type})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="su-form-group">
                                                    <label>Duração</label>
                                                    <select value={selectedDuration} onChange={e => setSelectedDuration(e.target.value)}>
                                                        {DURATION_OPTIONS.map(d => (
                                                            <option key={d.value} value={d.value}>{d.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            {grantMsg && (
                                                <div className={`su-grant-msg ${grantMsg.ok ? 'ok' : 'err'}`}>
                                                    {grantMsg.ok ? '✓ ' : '✗ '}{grantMsg.text}
                                                </div>
                                            )}
                                            <button className="su-btn primary full-width"
                                                disabled={!selectedProduct || grantLoading}
                                                onClick={handleGrant}>
                                                {grantLoading
                                                    ? <><div className="su-spinner" style={{ width: 13, height: 13, borderWidth: 2 }} /> Concedendo...</>
                                                    : <><Plus size={14} /> Conceder Acesso</>}
                                            </button>
                                        </div>

                                        {/* Lista ativa */}
                                        <p className="su-section-label">Ativos ({subProfile.activePurchases.length})</p>
                                        {subProfile.activePurchases.length === 0 ? (
                                            <div className="su-empty-state">Nenhum produto ativo</div>
                                        ) : (
                                            <div className="su-purchase-list">
                                                {subProfile.activePurchases.map(p => (
                                                    <div key={p._id} className="su-purchase-item">
                                                        <div className="su-purchase-dot" style={{ background: TYPE_COLOR[p.productId?.type || ''] || '#6b7280' }} />
                                                        <div className="su-purchase-info">
                                                            <div className="su-purchase-name">{productLabel(p.productId)}</div>
                                                            <div className="su-purchase-meta">
                                                                {p.accessType === 'lifetime'
                                                                    ? <span className="su-lifetime">Vitalício</span>
                                                                    : <span><Clock size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />Expira {fmtDate(p.expiresAt)}</span>}
                                                                <span style={{ opacity: 0.55 }}>Concedido {fmtDate(p.grantedAt || p.createdAt)}</span>
                                                            </div>
                                                        </div>
                                                        <button className="su-btn danger" style={{ fontSize: '0.74rem', padding: '5px 9px' }}
                                                            onClick={() => handleRevoke(p._id)}>
                                                            <Ban size={11} /> Revogar
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Histórico */}
                                        {subProfile.expiredPurchases.length > 0 && (<>
                                            <p className="su-section-label" style={{ marginTop: 20 }}>
                                                Histórico ({subProfile.expiredPurchases.length})
                                            </p>
                                            <div className="su-purchase-list">
                                                {subProfile.expiredPurchases.map(p => (
                                                    <div key={p._id} className="su-purchase-item expired">
                                                        <div className="su-purchase-dot" style={{ background: '#9ca3af' }} />
                                                        <div className="su-purchase-info">
                                                            <div className="su-purchase-name">{productLabel(p.productId)}</div>
                                                            <div className="su-purchase-meta">
                                                                {isExpired(p.expiresAt) ? `Expirou ${fmtDate(p.expiresAt)}` : 'Revogado'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>)}
                                    </div>
                                )}

                                {/* ── Features ────────────────────── */}
                                {subTab === 'features' && (
                                    <div>
                                        {subProfile.access.hasFullAccess && (
                                            <div className="su-info-box green">
                                                <strong>Acesso total ativo</strong> — todas as features são liberadas automaticamente.
                                                Revocações manuais ainda prevalecem se necessário.
                                            </div>
                                        )}
                                        {subProfile.user.isLegacyUser && (
                                            <div className="su-info-box amber">
                                                <strong>Usuário legado</strong> — acesso completo por padrão.
                                            </div>
                                        )}
                                        <table className="su-features-table">
                                            <thead>
                                                <tr>
                                                    <th>Funcionalidade</th>
                                                    <th>Status</th>
                                                    <th>Origem</th>
                                                    <th>Ação</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {FEATURES.map(feature => {
                                                    const ef = subProfile.effectiveFeatures[feature];
                                                    const granted = (subProfile.user.grantedFeatures || []).includes(feature);
                                                    return (
                                                        <tr key={feature}>
                                                            <td style={{ fontWeight: 600 }}>{FEATURE_LABELS[feature]}</td>
                                                            <td>
                                                                {ef.status === 'active' || ef.status === 'granted'
                                                                    ? <span className="su-badge active">Ativo</span>
                                                                    : ef.status === 'revoked'
                                                                        ? <span className="su-badge inactive">Revogado</span>
                                                                        : <span className="su-badge inactive">Bloqueado</span>}
                                                            </td>
                                                            <td style={{ fontSize: '0.77rem', color: 'var(--sub-text-muted)' }}>
                                                                {SOURCE_LABEL[ef.source] || ef.source}
                                                            </td>
                                                            <td>
                                                                <div style={{ display: 'flex', gap: 5 }}>
                                                                    <button className="su-btn ghost"
                                                                        style={{ fontSize: '0.73rem', padding: '4px 9px' }}
                                                                        disabled={granted || featureLoading !== null}
                                                                        onClick={() => doFeature(subModal._id, 'grant', feature)}>
                                                                        {featureLoading === `grant-${feature}` ? '...' : 'Conceder'}
                                                                    </button>
                                                                    <button className="su-btn danger"
                                                                        style={{ fontSize: '0.73rem', padding: '4px 9px' }}
                                                                        disabled={featureLoading !== null}
                                                                        onClick={() => doFeature(subModal._id, 'revoke', feature)}>
                                                                        {featureLoading === `revoke-${feature}` ? '...' : 'Revogar'}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                            </div>
                        </>) : (
                            <div className="su-loading" style={{ minHeight: 160 }}>
                                Não foi possível carregar o perfil.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubUsuarios;
